/*
 * CD CHANGES (Horizon core file — keep minimal):
 *   updateLayout()/#animateLayoutChange now set the grid's
 *   `product-grid-view` attribute SYNCHRONOUSLY instead of only inside the
 *   view-transition callback.
 *
 *   Bug it fixes: on mobile, toggling back from the "focus" 1-up view to
 *   the 2-up default could leave the grid stuck at 1-up. The attribute was
 *   only set inside the callback passed to startViewTransition; that
 *   utility's returned promise resolves solely via
 *   `transition.finished.then(...)` (no reject handler), so a
 *   skipped/aborted/rejected transition (a fast second tap, reduced-motion,
 *   a no-diff transition that the UA discards) could mean the callback's
 *   effect never landed and the promise hung — `product-grid-view` stayed
 *   `mobile-single`, so the CSS column rule for `default` (cd-facets.css
 *   §12) never matched and columns never reverted to 2-up.
 *
 *   Setting the attribute up front makes the column count deterministic in
 *   both directions on every device. The grid cross-fade is dropped (the
 *   subsequent transition would see no DOM diff) — acceptable polish; the
 *   end state for desktop (4-up <-> 2-up) is unchanged.
 */
import { mediaQueryLarge } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // CD: set the layout synchronously (see file header for the why).
    this.#setLayout(value);

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    sessionStorage.setItem(`product-grid-view-${viewport}`, value);
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
